import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export interface BankAccountDetails {
  bank_name: string;
  bank_address: string;
  account_number: string;
  opened_at: string;
}

interface BankAccountDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (details: BankAccountDetails) => Promise<void>;
  isSubmitting: boolean;
}

export function BankAccountDetailsDialog({
  open,
  onClose,
  onSubmit,
  isSubmitting,
}: BankAccountDetailsDialogProps) {
  const [bankName, setBankName] = useState("");
  const [bankAddress, setBankAddress] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  const isValid = bankName.trim() && bankAddress.trim() && accountNumber.trim();

  const handleSubmit = async () => {
    if (!isValid) return;

    await onSubmit({
      bank_name: bankName.trim(),
      bank_address: bankAddress.trim(),
      account_number: accountNumber.trim(),
      opened_at: new Date().toISOString(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bank Account Details</DialogTitle>
          <DialogDescription>
            Enter your bank account information. This will be recorded in official minutes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bankName">Bank Name <span className="text-destructive">*</span></Label>
            <Input
              id="bankName"
              placeholder="e.g., Chase Bank, Wells Fargo"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bankAddress">Bank Branch Address <span className="text-destructive">*</span></Label>
            <Input
              id="bankAddress"
              placeholder="e.g., 123 Main St, Los Angeles, CA 90001"
              value={bankAddress}
              onChange={(e) => setBankAddress(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="accountNumber">Account Number <span className="text-destructive">*</span></Label>
            <Input
              id="accountNumber"
              placeholder="Enter your account number"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Confirm & Generate Minutes"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
